/**
 * RecentCritiquesList Component
 * Feature: 067-recent-critiques
 *
 * Displays a vertical list of critique sessions the user has recently visited.
 * Each session shows collection info, session title, and recent comments.
 * Reuses CritiqueSessionListItem for consistent display.
 */

import { useRecentCritiqueSessionsGraphQL } from '../../../graphql/hooks/useCritiquesGraphQL';
import { CritiqueSessionListItem } from '../CritiqueSessionListItem/CritiqueSessionListItem';
import { PageHeader, PageHeaderLabel } from '@/components/PageHeader';
import { CritiqueIcon } from '@/components/icons';

import logger from '../../../utils/logger';

export interface RecentCritiquesListProps {
  className?: string;
}

/**
 * List container for recently viewed critique sessions
 *
 * Fetches and displays critique sessions the user has recently visited,
 * excluding sessions on collections they own (those appear in My Critiques).
 *
 * @param className - Optional CSS classes for styling
 *
 * @example
 * ```tsx
 * <RecentCritiquesList className="bg-gray-100" />
 * ```
 */
export function RecentCritiquesList({ className = '' }: RecentCritiquesListProps) {
  const { data, isLoading, error } = useRecentCritiqueSessionsGraphQL();

  // Loading state
  if (isLoading) {
    logger.debug('RecentCritiquesList', 'Loading recent discussions...');
    return (
      <div className={`w-full h-full min-h-0 overflow-y-auto flex items-center justify-center ${className}`}>
        <div className="text-gray-500">Loading recent discussions...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    logger.error('RecentCritiquesList', 'Failed to load recent discussions', error);
    return (
      <div className={`w-full h-full min-h-0 overflow-y-auto flex items-center justify-center ${className}`}>
        <div className="text-red-600">Failed to load recent discussions. Please try again.</div>
      </div>
    );
  }

  // Empty state
  if (!data?.sessions || data.sessions.length === 0) {
    logger.debug('RecentCritiquesList', 'No recent discussions found');
    return (
      <div className={`w-full h-full min-h-0 overflow-y-auto flex items-center justify-center ${className}`}>
        <div className="text-center text-gray-500 px-4">
          <p className="text-lg font-medium mb-2">No recently visited discussions</p>
          <p className="text-sm">Visit discussions to see them here.</p>
        </div>
      </div>
    );
  }

  // Sessions list
  logger.info('RecentCritiquesList', `Rendering ${data.sessions.length} recent discussions`);

  return (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Header with icon and title */}
      <PageHeader>
        <PageHeaderLabel
          title="Recent Discussions"
          icon={<CritiqueIcon size={32} />}
        />
      </PageHeader>

      {/* Session list */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
        {data.sessions.map((session) => (
          <CritiqueSessionListItem key={session.globalId} session={session} variant="recent" />
        ))}
      </div>
    </div>
  );
}
